import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [flasks, setFlasks] = useState([]);
  const [doses, setDoses] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const getDoseId = (dose) => dose?.id ?? dose?._id;
  const getFlaskId = (flask) => flask?.id ?? flask?._id;

  // Parse seguro para datas: aceita "YYYY-MM-DD" e ISO
  const parseDateSafe = (dateValue) => {
    if (!dateValue) return null;

    if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [y, m, d] = dateValue.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const d = new Date(dateValue);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDateBR = (dateValue) => {
    const d = parseDateSafe(dateValue);
    if (!d) return "—";
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateBRLong = (dateValue) => {
    const d = parseDateSafe(dateValue);
    if (!d) return "—";
    return d.toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const hasMeaningfulValue = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim() !== "";
    return true;
  };

  const toNumberOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // normaliza status de frasco (compatibilidade com versões antigas)
  const normalizeFlaskStatus = (s) => {
    if (!s) return "DISPONIVEL";
    const upper = String(s).toUpperCase();

    if (upper === "ATIVO") return "DISPONIVEL";
    if (upper === "EM USO" || upper === "EM_USO") return "EM_USO";
    if (upper === "FINALIZADO" || upper === "VAZIO") return "UTILIZADO";

    return upper;
  };

  // Busca dados do dashboard
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError("");

        const [profileRes, flasksRes, dosesRes] = await Promise.all([
          api.get("/users/profile"),
          api.get("/flasks"),
          api.get("/doses"),
        ]);

        const profileData = profileRes?.data?.profile ?? null;
        setProfile(profileData);

        setFlasks(Array.isArray(flasksRes?.data) ? flasksRes.data : []);
        setDoses(Array.isArray(dosesRes?.data) ? dosesRes.data : []);

        // ✅ Primeiro acesso: se não tiver nenhum campo preenchido, manda pro perfil
        const hasAnyProfileField =
          !!profileData &&
          (hasMeaningfulValue(profileData.startWeight) ||
            hasMeaningfulValue(profileData.goalWeight) ||
            hasMeaningfulValue(profileData.height) ||
            hasMeaningfulValue(profileData.gender) ||
            hasMeaningfulValue(profileData.ageRange));

        if (!hasAnyProfileField) {
          navigate("/app/profile", { replace: true });
        }
      } catch (err) {
        const apiMessage = err?.response?.data?.message;
        setError(
          apiMessage ||
            "Não foi possível carregar os dados do dashboard. Verifique seu perfil e tente novamente."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // ─────────────────────────────────────────────
  // Doses: úteis para análises
  // ─────────────────────────────────────────────
  const appliedDoses = useMemo(() => {
    const list = Array.isArray(doses) ? doses : [];
    return list
      .filter((d) => d?.status === "APLICADA" && d?.weight != null && d?.date)
      .sort((a, b) => {
        const da = parseDateSafe(a.date)?.getTime() ?? 0;
        const db = parseDateSafe(b.date)?.getTime() ?? 0;
        return da - db;
      });
  }, [doses]);

  const latestAppliedDose = useMemo(() => {
    if (!appliedDoses.length) return null;
    return appliedDoses[appliedDoses.length - 1];
  }, [appliedDoses]);

  const currentWeightFromDose = useMemo(() => {
    const w = Number(latestAppliedDose?.weight);
    return Number.isFinite(w) ? w : null;
  }, [latestAppliedDose]);

  const currentWeightDate = latestAppliedDose?.date || null;

  // Próxima dose planejada
  const nextPlannedDose = useMemo(() => {
    const list = Array.isArray(doses) ? doses : [];
    return list
      .filter((d) => d?.status === "PLANEJADA" && d?.date)
      .sort((a, b) => {
        const da = parseDateSafe(a.date);
        const db = parseDateSafe(b.date);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      })[0];
  }, [doses]);

  // ─────────────────────────────────────────────
  // IMC: inicial x atual + tendência
  // ─────────────────────────────────────────────
  const heightMeters = useMemo(() => {
    const h = Number(profile?.height);
    if (!Number.isFinite(h) || h <= 0) return null;
    return h / 100;
  }, [profile]);

  const imcStart = useMemo(() => {
    const sw = Number(profile?.startWeight);
    if (!heightMeters || !Number.isFinite(sw) || sw <= 0) return null;
    return sw / (heightMeters * heightMeters);
  }, [profile, heightMeters]);

  const imcCurrent = useMemo(() => {
    const cw = Number(currentWeightFromDose);
    if (!heightMeters || !Number.isFinite(cw) || cw <= 0) return null;
    return cw / (heightMeters * heightMeters);
  }, [currentWeightFromDose, heightMeters]);

  const imcTrend = useMemo(() => {
    if (!heightMeters) return null;
    if (appliedDoses.length < 2) return null;

    const last = appliedDoses[appliedDoses.length - 1];
    const prev = appliedDoses[appliedDoses.length - 2];

    const lastW = Number(last?.weight);
    const prevW = Number(prev?.weight);
    if (![lastW, prevW].every(Number.isFinite)) return null;

    const lastImc = lastW / (heightMeters * heightMeters);
    const prevImc = prevW / (heightMeters * heightMeters);

    const diff = lastImc - prevImc;
    if (Math.abs(diff) < 0.05) return { label: "Estável", symbol: "→", diff: 0 };
    if (diff < 0) return { label: "Reduzindo", symbol: "↓", diff };
    return { label: "Aumentando", symbol: "↑", diff };
  }, [appliedDoses, heightMeters]);

  // ─────────────────────────────────────────────
  // Progresso de peso (início -> meta)
  // ─────────────────────────────────────────────
  const progressPercentage = useMemo(() => {
    const start = Number(profile?.startWeight);
    const current = Number(currentWeightFromDose);
    const goal = Number(profile?.goalWeight);

    if (![start, current, goal].every(Number.isFinite)) return 0;
    if (goal >= start) return 0;

    const totalLossGoal = start - goal;
    const currentLoss = start - current;

    if (totalLossGoal <= 0 || currentLoss < 0) return 0;

    const percentage = (currentLoss / totalLossGoal) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  }, [profile, currentWeightFromDose]);

  const totalWeightLoss = useMemo(() => {
    const start = Number(profile?.startWeight);
    const current = Number(currentWeightFromDose);
    if (![start, current].every(Number.isFinite)) return null;
    return start - current;
  }, [profile, currentWeightFromDose]);

  // ─────────────────────────────────────────────
  // Gráfico: evolução do peso por semana
  // ─────────────────────────────────────────────
  const weeklyWeightSeries = useMemo(() => {
    if (!appliedDoses.length) return [];

    const getWeekKey = (dateValue) => {
      const d0 = parseDateSafe(dateValue);
      if (!d0) return null;

      const d = new Date(Date.UTC(d0.getFullYear(), d0.getMonth(), d0.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);

      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);

      const yyyy = d.getUTCFullYear();
      const ww = String(weekNo).padStart(2, "0");
      return `${yyyy}-W${ww}`;
    };

    const map = new Map();

    for (const dose of appliedDoses) {
      const key = getWeekKey(dose.date);
      if (!key) continue;
      const w = Number(dose.weight);
      if (!Number.isFinite(w)) continue;
      map.set(key, { key, weight: w, date: dose.date });
    }

    const arr = Array.from(map.values()).sort((a, b) => (a.key > b.key ? 1 : -1));
    return arr.slice(Math.max(0, arr.length - 10));
  }, [appliedDoses]);

  // ─────────────────────────────────────────────
  // Estoque real (mg) e projeção de falta
  // ─────────────────────────────────────────────
  const stockSummary = useMemo(() => {
    const list = Array.isArray(flasks) ? flasks : [];

    const usable = list
      .map((f) => {
        const st = normalizeFlaskStatus(f?.status);
        const remainingMg =
          toNumberOrNull(f?.remainingMg) ??
          toNumberOrNull(f?.totalMgAvailable) ??
          toNumberOrNull(f?.totalMg) ??
          toNumberOrNull(f?.totalDosage) ??
          0;

        return { ref: f, status: st, remainingMg };
      })
      .filter((x) => (x.status === "DISPONIVEL" || x.status === "EM_USO") && x.remainingMg > 0);

    const totalMg = usable.reduce((sum, x) => sum + (x.remainingMg || 0), 0);

    const countDisponivel = usable.filter((x) => x.status === "DISPONIVEL").length;
    const countEmUso = usable.filter((x) => x.status === "EM_USO").length;

    return {
      usable,
      totalMg,
      countDisponivel,
      countEmUso,
    };
  }, [flasks]);

  const flaskNeedForecast = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const futurePlanned = (Array.isArray(doses) ? doses : [])
      .filter((d) => d?.status === "PLANEJADA" && d?.date)
      .map((d) => ({
        ...d,
        _t: parseDateSafe(d.date)?.getTime() ?? 0,
        _mg: Number(d.dosage),
      }))
      .filter((d) => d._t >= today.getTime() && Number.isFinite(d._mg) && d._mg > 0)
      .sort((a, b) => a._t - b._t);

    if (!futurePlanned.length) {
      return {
        ok: true,
        message: "Sem doses futuras planejadas para calcular necessidade de novo frasco.",
        needAtDose: null,
        deficit: 0,
        stockMg: stockSummary.totalMg || 0,
      };
    }

    const totalMgInStock = stockSummary.totalMg || 0;

    if (!Number.isFinite(totalMgInStock) || totalMgInStock <= 0) {
      return {
        ok: false,
        message:
          "Sem frascos DISPONÍVEIS/EM USO (com mg restante). Cadastre um frasco ou marque como DISPONIVEL.",
        needAtDose: futurePlanned[0],
        deficit: null,
        stockMg: 0,
      };
    }

    let cum = 0;
    for (const d of futurePlanned) {
      cum += d._mg;
      if (cum > totalMgInStock) {
        return {
          ok: false,
          message: "Pela projeção, será necessário adquirir um novo frasco antes desta dose.",
          needAtDose: d,
          deficit: cum - totalMgInStock,
          stockMg: totalMgInStock,
        };
      }
    }

    return {
      ok: true,
      message:
        "Pela projeção, seus frascos DISPONÍVEIS/EM USO cobrem todas as doses planejadas.",
      needAtDose: null,
      deficit: 0,
      stockMg: totalMgInStock,
    };
  }, [doses, stockSummary]);

  const renderBarChart = (series) => {
    if (!series.length) {
      return (
        <p className="text-neutral-gray text-sm">
          Sem dados suficientes para exibir o gráfico.
        </p>
      );
    }

    const values = series.map((s) => s.weight);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(max - min, 0.1);

    return (
      <div className="w-full">
        <div className="flex items-end gap-2 h-40">
          {series.map((s) => {
            const h = ((s.weight - min) / range) * 100;
            return (
              <div key={s.key} className="flex-1 flex flex-col items-center justify-end">
                <div
                  className="w-full rounded-lg"
                  style={{
                    height: `${Math.max(10, h)}%`,
                    backgroundColor: "rgba(59,130,246,0.35)",
                  }}
                  title={`${s.weight} kg • ${formatDateBR(s.date)}`}
                />
                <span className="text-[10px] text-neutral-gray mt-2">
                  {s.key.slice(5)}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-neutral-gray mt-3">
          Último peso registrado em cada semana (últimas {series.length} semanas)
        </p>
      </div>
    );
  };

  if (isLoading) {
    return <p className="text-center text-neutral-gray">Carregando dashboard...</p>;
  }

  return (
    <>
      <h1 className="text-3xl font-bold text-deep-blue mb-8">Olá!</h1>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {profile && (
            <div className="bg-gradient-to-r from-blue-500 to-soft-blue p-6 rounded-2xl shadow-lg text-white">
              <h2 className="font-bold text-lg mb-4">Seu Progresso</h2>

              <div className="flex justify-between items-end mb-2 flex-wrap gap-2">
                <span>
                  Início: {hasMeaningfulValue(profile.startWeight) ? `${profile.startWeight} kg` : "-"}
                </span>

                <span className="font-bold text-2xl">
                  Atual: {currentWeightFromDose != null ? `${currentWeightFromDose} kg` : "-"}
                </span>

                <span>
                  Meta: {hasMeaningfulValue(profile.goalWeight) ? `${profile.goalWeight} kg` : "-"}
                </span>
              </div>

              <p className="text-sm opacity-90 mb-3">
                {currentWeightFromDose != null && currentWeightDate
                  ? `Peso atual registrado na dose do dia ${formatDateBR(currentWeightDate)}`
                  : "Registre o peso ao marcar uma dose como aplicada."}
              </p>

              <div className="w-full bg-blue-300 rounded-full h-2.5">
                <div
                  className="bg-white h-2.5 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="text-right mt-1 text-sm">{Math.round(progressPercentage)}% Completo</p>
            </div>
          )}

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="font-bold text-lg text-deep-blue">Rx do Tratamento</h2>

              {totalWeightLoss != null && (
                <div className="px-4 py-2 rounded-2xl bg-blue-50 text-deep-blue text-sm font-bold">
                  {totalWeightLoss >= 0
                    ? `Total reduzido: ${totalWeightLoss.toFixed(1)} kg`
                    : `Aumento: ${Math.abs(totalWeightLoss).toFixed(1)} kg`}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border rounded-xl p-4">
                <h3 className="font-bold text-deep-blue mb-2">Evolução do peso por semana</h3>
                {renderBarChart(weeklyWeightSeries)}
              </div>

              <div className="border rounded-xl p-4">
                <h3 className="font-bold text-deep-blue mb-2">Evolução do IMC</h3>

                {!heightMeters ? (
                  <p className="text-neutral-gray text-sm">
                    Para calcular IMC, preencha sua <b>altura</b> no Perfil.
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="text-sm text-neutral-gray">
                        <p>IMC início</p>
                        <p className="text-2xl font-bold text-deep-blue">
                          {imcStart != null ? imcStart.toFixed(1) : "—"}
                        </p>
                      </div>

                      <div className="text-sm text-neutral-gray">
                        <p>IMC atual</p>
                        <p className="text-2xl font-bold text-deep-blue">
                          {imcCurrent != null ? imcCurrent.toFixed(1) : "—"}
                        </p>
                      </div>

                      <div className="text-sm text-neutral-gray">
                        <p>Tendência</p>
                        <p className="text-2xl font-bold text-deep-blue">
                          {imcTrend ? `${imcTrend.symbol}` : "—"}
                        </p>
                        <p className="text-xs text-neutral-gray">
                          {imcTrend
                            ? imcTrend.label
                            : "Registre pelo menos 2 doses aplicadas com peso"}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-neutral-gray mt-3">
                      (Cálculo baseado na altura do Perfil e no peso registrado nas doses aplicadas.)
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <h2 className="font-bold text-lg text-deep-blue mb-4">Próxima Ação</h2>

            {nextPlannedDose ? (
              <div>
                <p className="text-neutral-gray">Próxima dose planejada:</p>
                <p className="text-2xl font-bold text-deep-blue my-2">
                  {formatDateBRLong(nextPlannedDose.date)}
                  <span className="text-soft-blue"> • {nextPlannedDose.dosage} mg</span>
                </p>
                <p className="text-sm text-neutral-gray">
                  Vá em <b>Histórico de Doses</b> para marcar como aplicada e registrar o peso.
                </p>
              </div>
            ) : (
              <p className="text-neutral-gray text-center py-4">
                Ótimo! Nenhuma dose futura planejada.
              </p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl shadow-md">
            <h2 className="font-bold text-lg text-deep-blue mb-4">Indicadores de Saúde</h2>

            <div className="text-center">
              <p className="text-neutral-gray">IMC Atual</p>
              <p className="text-4xl font-bold text-deep-blue my-2">
                {imcCurrent != null ? imcCurrent.toFixed(1) : "N/A"}
              </p>
              <p className="text-sm text-neutral-gray">
                {imcCurrent == null
                  ? "Preencha altura no Perfil e registre peso nas doses aplicadas."
                  : ""}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <h2 className="font-bold text-lg text-deep-blue mb-4">Estoque</h2>

            <div className="text-neutral-gray text-sm space-y-1">
              <p>
                Frascos cadastrados: <b className="text-deep-blue">{flasks.length}</b>
              </p>
              <p>
                Disponíveis: <b className="text-deep-blue">{stockSummary.countDisponivel}</b> •
                Em uso: <b className="text-deep-blue">{stockSummary.countEmUso}</b>
              </p>
              <p>
                Estoque (mg) considerando <b>DISPONÍVEL/EM USO</b>:{" "}
                <b className="text-deep-blue">
                  {Number(stockSummary.totalMg || 0).toFixed(1)}
                </b>
              </p>
            </div>

            <div
              className={`mt-4 p-4 rounded-xl border ${
                flaskNeedForecast.ok
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              <p
                className={`text-sm font-bold ${
                  flaskNeedForecast.ok ? "text-green-800" : "text-amber-800"
                }`}
              >
                {flaskNeedForecast.ok
                  ? "Projeção de estoque"
                  : "Atenção: possível falta de frasco"}
              </p>

              <p className="text-sm text-neutral-gray mt-1">
                {flaskNeedForecast.message}
              </p>

              {flaskNeedForecast.needAtDose?.date && (
                <p className="text-sm text-deep-blue mt-2">
                  Dose crítica: <b>{formatDateBR(flaskNeedForecast.needAtDose.date)}</b> •{" "}
                  <b>{Number(flaskNeedForecast.needAtDose.dosage).toFixed(1)} mg</b>
                </p>
              )}

              {Number.isFinite(flaskNeedForecast.stockMg) && (
                <p className="text-xs text-neutral-gray mt-2">
                  Estoque considerado (mg):{" "}
                  <b>{Number(flaskNeedForecast.stockMg).toFixed(1)}</b> •
                  (somente frascos DISPONÍVEL/EM USO, com mg restante)
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;